/**
 * Pure builder for the ⌘K command palette's v1 command set — web slice
 * (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md
 * §4, §11). Mirrors `apps/extension/src/sidepanel/paletteCommands.ts`
 * one-to-one in shape and ordering; web binds each command to its
 * existing PAGE-LEVEL verb instead of an IPC message — `markAllRead()`
 * (services/markRead), `dismissAllNotifications` (services/messages),
 * `webStorage` for the DND snooze key, `ClipboardCapabilities.writeText`,
 * and `navigate({ name: "settings" })`. No new services, no new
 * capability — every command binds a verb that already ships.
 *
 * Kept as its own module (not inlined in MainPage.tsx) so the command-set
 * assembly is unit-testable without mounting the full page. `MainPage`
 * calls this on every render with its live state, which satisfies spec
 * §5.3 ("state-dependent commands/labels computed at open time") — React's
 * render model means the array is always rebuilt from current state by
 * the time the palette opens.
 *
 * SECURITY: command labels are static action names — never notification
 * or clip content (spec §8). The copy-clip command's `run` writes the
 * clip's text to the OS clipboard but never surfaces it in a label/hint.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import type { ClipboardCapabilities, DockClip, PaletteCommand } from "@reflecto/client-ui";
import { ONE_HOUR_MS } from "@reflecto/client-ui";
import { webStorage } from "../adapters/storage.web";
import { messages } from "../i18n";
import { markAllRead } from "../services/markRead";
import { dismissAllNotifications } from "../services/messages";

export interface BuildPaletteCommandsInput {
  /** Full (unfiltered) notification list — the palette has no search query of its own. */
  notifications: DecryptedNotification[];
  /** Full (unfiltered) conversation thread list. */
  conversations: ConversationThread[];
  /** Same value that gates the header's "Mark all read" affordance (unread items, not just any items). */
  unreadCount: number;
  publicMode: boolean;
  onTogglePublicMode: () => void;
  /** Same AI-capability gate that shows the header's Glance button. */
  aiCapable: boolean;
  /** Bumps the host's `recapTrigger` counter. */
  onGlance: () => void;
  dndSnoozeUntil: number | undefined;
  /** Newest-first, as MainPage maps from the clip session store. */
  dockClips: DockClip[];
  clipCapabilities: Pick<ClipboardCapabilities, "writeText">;
  onOpenSettings: () => void;
  /** Injectable clock — tests pin this for deterministic snooze-write / is-snoozed assertions. */
  now?: () => number;
}

/**
 * Assemble the v1 command list, in the spec §4 default order. State-dependent
 * commands (Glance, cancel-snooze, copy-clip) are appended only when their
 * gate is satisfied; the privacy-toggle label reflects the current state.
 */
export function buildPaletteCommands(input: BuildPaletteCommandsInput): PaletteCommand[] {
  const {
    notifications,
    conversations,
    unreadCount,
    publicMode,
    onTogglePublicMode,
    aiCapable,
    onGlance,
    dndSnoozeUntil,
    dockClips,
    clipCapabilities,
    onOpenSettings,
    now = Date.now,
  } = input;

  const commands: PaletteCommand[] = [];

  // Mirrors the header's own "Mark all read" gate (`unreadCount > 0`) — hidden
  // entirely (not just disabled) when there's nothing to mark.
  if (unreadCount > 0) {
    commands.push({
      id: "mark-all-read",
      label: messages.list.markAllRead,
      run: () => {
        void markAllRead();
      },
    });
  }

  // Mirrors the list's own "Clear All" gate (hidden when there's nothing to clear).
  // Deliberately GLOBAL, unlike the list header's Clear-All: the palette has no
  // search query of its own, so this always dismisses every notification +
  // conversation thread, unfiltered — the header's Clear-All narrows to the
  // active search match, this command never does.
  if (notifications.length > 0 || conversations.length > 0) {
    commands.push({
      id: "clear-all",
      label: messages.palette.clearAll,
      run: () => {
        void dismissAllNotifications(
          notifications.map((n) => n.msgId),
          conversations.map((c) => c.key)
        );
      },
    });
  }

  commands.push({
    id: "toggle-privacy",
    label: publicMode
      ? messages.header.publicModeToggleDisable
      : messages.header.publicModeToggleEnable,
    run: onTogglePublicMode,
  });

  if (aiCapable) {
    commands.push({
      id: "glance",
      label: messages.palette.glance,
      run: onGlance,
    });
  }

  commands.push({
    id: "snooze-1h",
    // Only one preset exists (SettingsPage's DndEditor ships a single 1h snooze) —
    // reuse its exact label so the duration is never re-invented/duplicated across
    // the two surfaces.
    label: messages.dnd.snooze1h,
    run: () => {
      void webStorage.set({ dndSnoozeUntil: now() + ONE_HOUR_MS });
    },
  });

  if (dndSnoozeUntil != null && dndSnoozeUntil > now()) {
    commands.push({
      id: "cancel-snooze",
      label: messages.palette.cancelSnooze,
      run: () => {
        void webStorage.remove("dndSnoozeUntil");
      },
    });
  }

  const latestClip = dockClips[0];
  if (latestClip) {
    commands.push({
      id: "copy-clip",
      label: messages.palette.copyClip,
      run: () => {
        // Tap-to-write invariant: this only runs from an explicit palette Enter/click.
        // Errors (e.g. clipboard permission denied) are swallowed — same silent-fail
        // as ClipDock's own Copy button; there is no visible affordance on this row.
        clipCapabilities.writeText(latestClip.content).catch(() => {});
      },
    });
  }

  commands.push({
    id: "open-settings",
    label: messages.header.openSettings,
    run: onOpenSettings,
  });

  return commands;
}
